<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb title="My Schedule" text="Attendance" text1="My Schedule" />
      </div>

      <div class="card mb-3">
        <div class="card-body">
          <div class="row g-2 align-items-end">
            <div class="col-md-3">
              <label class="form-label">From</label>
              <input v-model="filters.date_from" type="date" class="form-control" />
            </div>
            <div class="col-md-3">
              <label class="form-label">To</label>
              <input v-model="filters.date_to" type="date" class="form-control" />
            </div>
            <div class="col-md-2 d-grid">
              <button class="btn btn-primary" :disabled="loading" @click="fetchAll({ page: 1 })">
                Filter
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">Published Shifts</h5>
        </div>
        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <a-table
              class="table datatable thead-light"
              :columns="columns"
              :data-source="items"
              :loading="loading"
              row-key="id"
            >
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'date'">
                  {{ formatDate(record.date) }}
                </template>
                <template v-else-if="column.key === 'shift'">
                  <div>
                    <div class="fw-medium">{{ record.shift_name || "Shift" }}</div>
                    <div class="text-muted fs-12">
                      {{ formatTime(record.start_time) }} – {{ formatTime(record.end_time) }}
                      <span v-if="record.break_minutes"> · Break {{ record.break_minutes }}m</span>
                    </div>
                  </div>
                </template>
                <template v-else-if="column.key === 'windows'">
                  <div class="fs-12 text-muted">
                    <div v-if="record.min_start_time || record.max_start_time">
                      Start: {{ formatTime(record.min_start_time) }}–{{ formatTime(record.max_start_time) }}
                    </div>
                    <div v-if="record.min_end_time || record.max_end_time">
                      End: {{ formatTime(record.min_end_time) }}–{{ formatTime(record.max_end_time) }}
                    </div>
                    <span v-if="!record.min_start_time && !record.max_start_time">—</span>
                  </div>
                </template>
                <template v-else-if="column.key === 'duration'">
                  {{ formatDuration(record.scheduled_minutes) }}
                </template>
              </template>
            </a-table>
          </div>
          <div class="p-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              Showing page {{ pagination.current_page }} of {{ totalPages }}
              <span v-if="pagination.total">({{ pagination.total }} shifts)</span>
            </div>
            <div class="d-flex gap-2">
              <button
                class="btn btn-outline-secondary btn-sm"
                :disabled="pagination.current_page <= 1 || loading"
                @click="fetchAll({ page: pagination.current_page - 1 })"
              >
                Prev
              </button>
              <button
                class="btn btn-outline-secondary btn-sm"
                :disabled="pagination.current_page >= totalPages || loading"
                @click="fetchAll({ page: pagination.current_page + 1 })"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import moment from "moment";
import employeeScheduleService from "@/services/hrm/employeeScheduleService";
import { toast } from "@/utils/toast";

const items = ref([]);
const loading = ref(false);
const pagination = ref({ total: 0, per_page: 25, current_page: 1 });
const filters = reactive({
  date_from: moment().startOf("month").format("YYYY-MM-DD"),
  date_to: moment().endOf("month").format("YYYY-MM-DD"),
  per_page: 25,
  page: 1,
});

const columns = [
  { title: "Date", key: "date", dataIndex: "date" },
  { title: "Shift", key: "shift", dataIndex: "shift_name" },
  { title: "Punch Windows", key: "windows" },
  { title: "Net Hours", key: "duration", dataIndex: "scheduled_minutes" },
];

const totalPages = computed(() => {
  const t = pagination.value.total || 0;
  const p = pagination.value.per_page || 25;
  return Math.max(1, Math.ceil(t / p));
});

function formatDate(d) {
  return d ? moment(d).format("ddd, DD MMM YYYY") : "-";
}

function formatTime(t) {
  if (!t) return "—";
  return moment(t, ["HH:mm:ss", "HH:mm"]).format("HH:mm");
}

function formatDuration(minutes) {
  const m = Number(minutes || 0);
  if (!m) return "—";
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

async function fetchAll(extra = {}) {
  loading.value = true;
  try {
    const params = { ...filters, ...extra };
    const { data } = await employeeScheduleService.list(params);
    if (data?.data) {
      items.value = data.data;
      pagination.value = {
        total: data.total ?? data.meta?.total ?? data.data.length,
        per_page: data.per_page ?? data.meta?.per_page ?? params.per_page,
        current_page: data.current_page ?? data.meta?.current_page ?? 1,
      };
    } else {
      items.value = Array.isArray(data) ? data : [];
    }
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to load schedule", "error");
  } finally {
    loading.value = false;
  }
}

onMounted(() => fetchAll());
</script>
